import { Section } from '~/components/common/section';
import { ResponsiveContainer, PieChart, Pie, Sector, Cell } from 'recharts';
import { Translate } from '~/i18n';
import usePayoutBreakdown from '~/apis/dashboard-statistics/usePayoutBreakdown';
import { SubmitLoader } from '~/components/common/loaders/submit';
import PayoutBreakdownNumbers from './payout-breakdown-numbers';
import { useCallback, useState } from 'react';
import EmptyState from './empty-states';
import { formatNumber } from '~/utils/format-price';

const PayoutPie = () => {
   const [activeIndex, setActiveIndex] = useState(0);
   const onPieEnter = useCallback(
      (_: any, index: number) => {
         setActiveIndex(index);
      },
      [setActiveIndex]
   );
   const { data: PayoutBreakdown, isLoading } = usePayoutBreakdown();

   const colorFill = (name: string) => {
      return name === 'paid' ? '#00983F' : name === 'ready_for_payout' ? '#3699FF' : '#FFA800';
   };
   const centeredText = (name: string) => {
      return name === 'paid' ? (
         <Translate id="payout:paid" />
      ) : name === 'ready_for_payout' ? (
         <Translate id="payout:ready for payout" />
      ) : (
         <Translate id="payout:payout requests" />
      );
   };

   if (isLoading) return <SubmitLoader />;
   const parsedBreakdown = { ...PayoutBreakdown?.data };
   delete parsedBreakdown?.show_create_claim_button;
   delete parsedBreakdown?.totals;
   delete parsedBreakdown?.currency;

   const breakdown = Object?.entries(parsedBreakdown)?.map((payout: any) => ({
      name: payout[0],
      value: Number(payout[1]),
   }));

   const renderActiveShape = (props: any) => {
      const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, payload } = props;

      return (
         <g>
            <text
               x={cx}
               y={cy}
               dy={0}
               textAnchor="middle"
               fill="#666666"
               className="text-3xl font-bold text-grey-200 ">
               {formatNumber(payload.value)} SR
            </text>
            <text x={cx} y={cy} dy={30} textAnchor="middle" fill="#999999">
               {centeredText(payload.name)}
            </text>
            <Sector
               cx={cx}
               cy={cy}
               innerRadius={innerRadius}
               outerRadius={outerRadius}
               startAngle={startAngle}
               endAngle={endAngle}
               fill={colorFill(payload.name)}
            />
         </g>
      );
   };

   return (
      <Section className=" py-8 h-full">
         <div className="px-5 mb-4">
            <span className="block text-lg font-semibold">
               <Translate id="dashboard:payout summary" />
            </span>
         </div>
         <div className=" flex flex-col lg:flex-row items-center h-full">
            {PayoutBreakdown?.data?.length === 0 ? (
               <div className="py-8 w-full">
                  <EmptyState />
               </div>
            ) : (
               <>
                  <div className="w-1/2">
                     <ResponsiveContainer aspect={1 / 1} className="px-5">
                        <PieChart>
                           <Pie
                              data={breakdown}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={115}
                              outerRadius={140}
                              fill="#82ca9d"
                              activeIndex={activeIndex}
                              activeShape={renderActiveShape}
                              onMouseEnter={onPieEnter}>
                              {breakdown.map((entry, index) => (
                                 <Cell key={`cell-${index}`} fill={colorFill(entry.name)} />
                              ))}
                           </Pie>
                        </PieChart>
                     </ResponsiveContainer>
                  </div>
                  <div className="w-full lg:w-1/2 px-5">
                     <PayoutBreakdownNumbers data={PayoutBreakdown?.data} />
                  </div>
               </>
            )}
         </div>
      </Section>
   );
};

export default PayoutPie;
